import { toPositiveOptionId } from '@/utils/optionValues';
import {
  SALE_MODAL_SECTIONS,
  createInitialSaleForm,
  createInitialSaleOptions,
} from '@/composables/sales/modal/useSaleModalForm';
import {
  buildFirstInstallmentOptions,
  createInitialPaymentPlanTaxPreview,
} from '@/composables/paymentPlans/usePaymentPlanModalForm';
import { SALES_TEXT } from '@/texts/catalog/sales';

export const createSaleModalState = () => ({
  sections: SALE_MODAL_SECTIONS,
  activeSectionKey: 'general',
  optionsLoading: false,
  saleCodeRequestToken: 0,
  quoteCatalogRequestToken: 0,
  quoteCatalogCustomerId: null,
  taxPreviewLoading: false,
  taxPreviewRequestToken: 0,
  taxPreviewDebounceTimer: null,
  taxPreviewDebounceMs: 350,
  consecutiveType: null,
  form: createInitialSaleForm(),
  options: createInitialSaleOptions(),
  firstInstallmentOptions: buildFirstInstallmentOptions(),
  taxPreview: createInitialPaymentPlanTaxPreview(),
  tagOptions: [],
});

const MODAL_TEXT = SALES_TEXT.modal;

export const saleModalComputed = {
  closeAriaLabel() {
    return MODAL_TEXT.closeAriaLabel;
  },
  sectionsAriaLabel() {
    return MODAL_TEXT.sectionsAriaLabel;
  },
  cancelButtonLabel() {
    return MODAL_TEXT.actions.cancel;
  },
  submittingLabel() {
    return MODAL_TEXT.actions.submitting;
  },
  isAdvertisingEnabled() {
    return Boolean(this.advertisingEnabled);
  },
  isMultiCustomerMode() {
    return String(this.customerMode || '').trim().toLowerCase() === 'multi';
  },
  isFlatRateMode() {
    if (this.isMultiCustomerMode) {
      return false;
    }

    return String(this.saleKind || '').trim().toLowerCase() === 'flat-rate';
  },
  resolvedServiceFilterMode() {
    const explicitMode = String(this.serviceFilterMode || '').trim().toLowerCase();
    if (explicitMode === 'flat-rate' || explicitMode === 'standard') {
      return explicitMode;
    }

    return this.isFlatRateMode ? 'flat-rate' : 'standard';
  },
  canSubmit() {
    if (this.isMultiCustomerMode) {
      const selectedCustomers = (Array.isArray(this.form.customers) ? this.form.customers : [])
        .map((customer) => toPositiveOptionId(customer))
        .filter((id) => id > 0);

      const hasCustomer = selectedCustomers.length > 0 || toPositiveOptionId(this.form.customer) > 0;
      const hasService = toPositiveOptionId(this.form.service) > 0;
      return hasCustomer && hasService && String(this.form.saleName || '').trim() !== '';
    }

    const hasCustomer = toPositiveOptionId(this.form.customer) > 0;
    const hasService = toPositiveOptionId(this.form.service) > 0;
    return hasCustomer && hasService && String(this.form.saleName || '').trim() !== '';
  },
  isEditMode() {
    return String(this.mode || 'create').trim().toLowerCase() === 'edit';
  },
  hasInitialData() {
    return Boolean(this.initialData && typeof this.initialData === 'object' && Object.keys(this.initialData).length > 0);
  },
  modalTitle() {
    if (this.isMultiCustomerMode) {
      return this.isEditMode ? MODAL_TEXT.title.multiCustomer.edit : MODAL_TEXT.title.multiCustomer.create;
    }

    if (this.isFlatRateMode) {
      return this.isEditMode ? MODAL_TEXT.title.flatRate.edit : MODAL_TEXT.title.flatRate.create;
    }

    return this.isEditMode ? MODAL_TEXT.title.business.edit : MODAL_TEXT.title.business.create;
  },
  modalSubtitle() {
    if (this.isMultiCustomerMode) {
      return this.isEditMode ? MODAL_TEXT.subtitle.multiCustomer.edit : MODAL_TEXT.subtitle.multiCustomer.create;
    }

    if (this.isFlatRateMode) {
      return this.isEditMode ? MODAL_TEXT.subtitle.flatRate.edit : MODAL_TEXT.subtitle.flatRate.create;
    }

    return this.isEditMode ? MODAL_TEXT.subtitle.business.edit : MODAL_TEXT.subtitle.business.create;
  },
  submitButtonLabel() {
    return this.isEditMode ? MODAL_TEXT.actions.submitEdit : MODAL_TEXT.actions.submitCreate;
  },
  deleteButtonLabel() {
    const deleteText = MODAL_TEXT?.actions?.delete;

    if (this.isMultiCustomerMode) {
      return String(deleteText?.multiCustomer || 'Eliminar negocio multicliente');
    }

    if (this.isFlatRateMode) {
      return String(deleteText?.flatRate || 'Eliminar iguala');
    }

    return String(deleteText?.business || 'Eliminar negocio');
  },
  showCodeField() {
    if (this.consecutiveType === 'general') {
      return true;
    }

    if (this.consecutiveType === 'services') {
      return toPositiveOptionId(this.form.service) > 0;
    }

    return false;
  },
};
